import { PageContainer } from "../../../components/pageContainer/PageContainer";
import {
  Anchor,
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
  InputNumber,
} from "@sellar-io/components";
import {
  ContainerTypeEligibilityContainerTypesType,
  ContainerTypeEligibilityUnitTypesType,
  CourierDeliveryScheduleListItemResponseType,
  DeliveryChargeFulfillmentType,
} from "@sellar-io/api-v2/types";
import { formatCurrency, pluralize } from "@sellar-io/common";
import dayjs from "dayjs";
import { ReactElement, ReactNode, useState } from "react";
import { useMatch, useNavigate } from "react-router";
import { Icon } from "../../../components";
import { getFlatChargeBadge } from "../../../components/courierHaulierSwitchPrompt/pages/PalletDeliverySettingsPage.shared";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
} from "../../../components/table/TableLegacy";
import * as ROUTES from "../../../constants/routes";
import { NEW_DELIVERY_AREA } from "../../../constants/routes";
import { useCourierDeliveryScheduleList } from "../../../hooks/api/v2/courierDeliverySchedule/useCourierDeliveryScheduleList";
import { useFulfillmentTypeList } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";

import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { capitalize } from "../../../utils/nativeLodash";
import { deliveryDays } from "../../deliveryAreas/DeliveryAreaForm.shared";
import { DeliveryAreaListTable } from "../../deliveryAreas/DeliveryAreaListTable";
import { ContainerTypeDeliveryChargeRulesCard } from "../containerDeliveryChargeRules/cards/ContainerTypeDeliveryChargeRulesCard";
import { CourierDeliveryScheduleActionPanel } from "../CourierDeliverySchedule/CourierDeliveryScheduleActionPanel";
import { FulfillmentTypeChargeCapUpdateDialog } from "../FulfillmentTypeContainerDeliveryCharge/dialogs/FulfillmentTypeChargeCapUpdateDialog";
import { FulfillmentTypeContainerDeliveryChargeCreateDialog } from "../FulfillmentTypeContainerDeliveryCharge/dialogs/FulfillmentTypeContainerDeliveryChargeCreateDialog";
import { FulfillmentTypeUpdateDialog } from "../FulfillmentTypeContainerDeliveryCharge/dialogs/FulfillmentTypeUpdateDialog";
import { FulfillmentTypeContainerDeliveryChargeTableRow } from "../FulfillmentTypeContainerDeliveryCharge/FulfillmentTypeContainerDeliveryChargeTableRow";
import { CourierScheduleCreateDialog } from "../modals/CourierScheduleCreateDialog";
import { CourierScheduleUpdateDialog } from "../modals/CourierScheduleUpdateDialog";
import { FreeDeliveryChargeRuleCard } from "./FreeDeliveryChargeRuleCard";
import {
  InputLabel,
  InputContainer,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import { useContainerTypeEligibilityRulesCreate } from "../../../hooks/api/v2/containerTypeEligibilityRules/useContainerTypeEligibilityRulesCreate";
import { useContainerTypeEligibilityRulesUpdate } from "../../../hooks/api/v2/containerTypeEligibilityRules/useContainerTypeEligibilityRulesUpdate";
import { useContainerTypeEligibilityRulesDelete } from "../../../hooks/api/v2/containerTypeEligibilityRules/useContainerTypeEligibilityRulesDelete";
import { twMerge } from "tailwind-merge";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { BadgeAmber } from "../../../components/badge/Badge";
import { useMediaQuery } from "usehooks-ts";
import {
  eligibilityTypes,
  CourierEligibilityRuleTable,
} from "./ForDeliveryMethod.shared";

export const ForDeliveryMethod = (): ReactElement => {
  const { business } = useSellarCurrentBusiness();
  if (!business) throw new Error("Business should be defined");

  const method = useMatch(ROUTES.DELIVERY_METHOD_CHARGES.pattern)?.params
    .method;

  if (!method) {
    throw new Error("Cannot find method");
  }

  if (method !== "direct" && method !== "courier" && method !== "haulier") {
    throw new Error("Unknown delivery method");
  }

  const [
    fulfillmentTypeContainerDeliveryChargeDialogActive,
    setFulfillmentTypeContainerDeliveryChargeDialogActive,
  ] = useState<boolean>(false);
  const [flatChargeActive, setFlatChargeActive] = useState<boolean>(false);
  const [chargeCapActive, setChargeCapActive] = useState<boolean>(false);
  const [courierScheduleActive, setCourierScheduleActive] =
    useState<boolean>(false);
  const [updateCourierScheduleActive, setUpdateCourierScheduleActive] =
    useState<boolean>(false);
  const isSmallScreen = useMediaQuery("(max-width: 480px)");

  const fulfillmentTypeQuery = useFulfillmentTypeList({
    query: [
      {
        type: "FulfillmentType",
        value: method as DeliveryChargeFulfillmentType,
        modifier: "EQUALS",
      },
    ],
    sortBy: "createdAt",
    sortDir: "asc",
  });

  const courierDeliveryScheduleQuery = useCourierDeliveryScheduleList({
    query: [],
    sortBy: "createdAt",
    sortDir: "desc",
  });

  const navigate = useNavigate();

  const fulfillmentTypePages = fulfillmentTypeQuery.isSuccess
    ? fulfillmentTypeQuery.data.pages
    : [];

  const fulfillmentTypeList = fulfillmentTypePages.flat();

  const containerTypeDeliveryChargeRulesList = fulfillmentTypeList.flatMap(
    (ft) => ft.ContainerTypeDeliveryChargeRules,
  );

  const containerTypeEligibilityRulesList = fulfillmentTypeList.flatMap(
    (ft) => ft.ContainerTypeEligibilityRules,
  );

  const rulesByContainerType = Object.fromEntries(
    containerTypeEligibilityRulesList.map((rule) => [rule.containerType, rule]),
  );

  const fulfillmentTypeId = fulfillmentTypePages[0]?.[0]?.id ?? undefined;

  const courierDeliverySchedule =
    courierDeliveryScheduleQuery.data?.pages[0]?.[0];

  const deliveryAreaDaysOrdered = (
    courierDeliveryAreaDays: CourierDeliveryScheduleListItemResponseType["CourierDeliveryScheduleDay"],
  ) => {
    const selectedDays = courierDeliveryAreaDays.map(
      (courierDeliveryScheduleDay) => courierDeliveryScheduleDay.day,
    );
    return selectedDays
      .sort((a, b) => {
        return (
          deliveryDays.findIndex((day) => day.value === a) -
          deliveryDays.findIndex((day) => day.value === b)
        );
      })
      .map((day) => pluralize(capitalize(day), 2));
  };

  const eligibilityGridCols = "grid-cols-[100px_1fr_120px_75px_50px]";

  const showNotSetWarning =
    method === "haulier" &&
    !!fulfillmentTypeQuery.data?.pages[0]?.[0]
      ?.FulfillmentTypeContainerDeliveryCharges &&
    fulfillmentTypeQuery.data.pages[0][0]
      .FulfillmentTypeContainerDeliveryCharges.length === 0;

  const getFlatDeliveryCharge = () => {
    const charge = fulfillmentTypeQuery.data?.pages[0]?.[0]?.charge;
    if (method !== "haulier") {
      return charge ? formatCurrency(charge, "gbp") : "Not set";
    }

    return getFlatChargeBadge(charge);
  };
  return (
    <>
      <NavigationBar
        title={`${capitalize(method === "haulier" ? "pallet" : method)} Tier`}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => navigate(ROUTES.DELIVERY_CHARGES)}
      />
      <PageContainer>
        <>
          {method === "direct" && (
            <Card className="bg-white">
              <CardHeader
                className={twMerge(
                  "bg-white",
                  "flex items-center justify-between p-4",
                )}
              >
                <div className="flex flex-col">
                  <H5>Delivery areas</H5>
                  <H6></H6>
                </div>
                <Button
                  color={"secondary"}
                  onClick={() => navigate(NEW_DELIVERY_AREA)}
                >
                  <Icon icon={"PLUS"} />
                  New
                </Button>
              </CardHeader>
              <CardContent className={"p-4"}>
                <DeliveryAreaListTable />
              </CardContent>
            </Card>
          )}
          <FreeDeliveryChargeRuleCard fulfillmentType={method} />
          <ContainerTypeDeliveryChargeRulesCard
            containerTypeDeliveryChargeRulesList={
              containerTypeDeliveryChargeRulesList
            }
            fulfillmentTypeId={fulfillmentTypeId}
            method={method}
          />
          {method === "courier" && (
            <Card className="bg-white">
              <CardHeader className={twMerge("bg-white", "flex flex-col p-4")}>
                <H5>Container eligibility rules</H5>
                <H6>
                  Declare the maximum keg and cask container volumes you're able
                  to fulfil by courier. Customers who wish to order items in
                  larger containers will be prompted to switch to haulier
                  delivery. Visit our{" "}
                  <Anchor
                    href="" // Awaiting link for go live
                    target="_blank"
                    rel="noreferrer"
                    className="underlin text-blue-500"
                  >
                    help centre
                  </Anchor>{" "}
                  for more information
                </H6>
              </CardHeader>
              <CourierEligibilityRuleTable
                mode={isSmallScreen ? "mobile" : "desktop"}
                desktop={
                  <CardContent className={"flex flex-col gap-4 p-4"}>
                    {fulfillmentTypeId && (
                      <div
                        className={twMerge(
                          "grid rounded-lg border border-zinc-300 text-sm",
                          eligibilityGridCols,
                        )}
                      >
                        <div
                          className={twMerge(
                            "col-span-5 grid rounded-t-lg bg-zinc-50 font-semibold",
                            eligibilityGridCols,
                          )}
                        >
                          <div className="rounded-tl-lg border-b border-zinc-300 p-2">
                            Status
                          </div>
                          <div className="border-b border-zinc-300 p-2">
                            Container type
                          </div>
                          <div className="border-b border-zinc-300 p-2 text-right">
                            Max. volume
                          </div>
                          <div className="border-b border-zinc-300 p-2 text-right">
                            Units
                          </div>
                          <div className="border-b border-zinc-300" />
                        </div>
                        {eligibilityTypes.map(
                          ({ display, containerType }, idx) => {
                            const rule = rulesByContainerType[containerType];

                            return (
                              <ContainerEligibilityRuleRowDesktop
                                key={containerType}
                                name={display}
                                containerType={containerType}
                                volume={rule?.volume}
                                unit={rule?.unit}
                                id={rule?.id}
                                FulfillmentTypeId={fulfillmentTypeId}
                                eligibilityGridCols={eligibilityGridCols}
                                border={idx + 1 < eligibilityTypes.length}
                              />
                            );
                          },
                        )}
                      </div>
                    )}
                    <div className="flex flex-col gap-2 rounded-lg bg-zinc-100 p-4 text-sm">
                      <span className="font-semibold">
                        Your courier container eligibility rules:
                      </span>
                      {eligibilityTypes.map(({ display, containerType }) => {
                        const rule = rulesByContainerType[containerType];

                        return (
                          <span
                            key={containerType}
                            className="flex items-center gap-2"
                          >
                            <Icon
                              icon="CIRCLE"
                              size={0.5}
                              className={twMerge(
                                rule ? "text-emerald-500" : "text-red-500",
                              )}
                            />
                            {rule
                              ? `${display} can be fulfilled by courier up to and including ${rule.volume}${rule.unit}`
                              : `${display} can only be fulfilled by haulier`}
                          </span>
                        );
                      })}
                    </div>
                  </CardContent>
                }
                mobile={
                  fulfillmentTypeId ? (
                    <CardContent className="p-4">
                      <div className="divide-y divide-zinc-300 rounded-lg border border-zinc-300">
                        {eligibilityTypes.map(({ display, containerType }) => {
                          const rule = rulesByContainerType[containerType];

                          return (
                            <ContainerEligibilityRuleRowMobile
                              key={containerType}
                              name={display}
                              containerType={containerType}
                              volume={rule?.volume}
                              unit={rule?.unit}
                              id={rule?.id}
                              FulfillmentTypeId={fulfillmentTypeId}
                            />
                          );
                        })}
                      </div>
                    </CardContent>
                  ) : null
                }
              />
            </Card>
          )}
          <Card className="bg-white">
            <CardHeader
              className={twMerge(
                "bg-white",
                "flex items-center justify-between p-4",
              )}
            >
              <div className="flex flex-col">
                <H5>Flat delivery charge</H5>
                <H6>
                  Apply a flat delivery charge to all orders in addition to any
                  delivery charges per product item
                </H6>
              </div>
              <Button
                color={"secondary"}
                onClick={() => setFlatChargeActive(true)}
              >
                <Icon icon={"PENCIL"} />
                Edit
              </Button>
            </CardHeader>
            <CardContent className={"flex flex-col gap-4 p-4"}>
              {getFlatDeliveryCharge()}
            </CardContent>
          </Card>
          {method === "courier" && (
            <Card className="bg-white">
              <CardHeader
                className={twMerge(
                  "bg-white",
                  "flex items-center justify-between p-4",
                )}
              >
                <div className="flex flex-col">
                  <H5>Delivery charge cap</H5>
                  <H6>
                    Set a delivery charge cap to show your customers the most
                    they can be charged for delivery for larger orders
                  </H6>
                </div>
                <Button
                  color={"secondary"}
                  onClick={() => setChargeCapActive(true)}
                >
                  <Icon icon={"PENCIL"} />
                  Edit
                </Button>
              </CardHeader>
              <CardContent className={"p-4"}>
                <p>
                  {fulfillmentTypeQuery.data?.pages[0]?.[0]?.deliveryChargeCap
                    ? formatCurrency(
                        fulfillmentTypeQuery.data.pages[0][0].deliveryChargeCap,
                        "gbp",
                      )
                    : "Not set"}
                </p>
              </CardContent>
            </Card>
          )}
          <Card className="bg-white">
            <CardHeader className={twMerge("bg-white", "flex flex-col p-4")}>
              <H5>Delivery charges</H5>
              <H6>
                {" "}
                Charge is applied per unit and excludes VAT. Adding multiple
                units of a product will increase delivery charge by the
                specified amount
              </H6>
            </CardHeader>
            <CardContent className={"p-4"}>
              <TableLegacyContainer className={"overflow-auto"}>
                <TableLegacy>
                  <TableLegacyTHead>
                    <TableLegacyTR>
                      <TableLegacyTH>Product format</TableLegacyTH>
                      <TableLegacyTH>Delivery charge</TableLegacyTH>
                      <TableLegacyTH />
                    </TableLegacyTR>
                  </TableLegacyTHead>
                  {fulfillmentTypeQuery.data?.pages.map((page) =>
                    page.map((fulfillmentType) => (
                      <>
                        {fulfillmentType.FulfillmentTypeContainerDeliveryCharges.map(
                          (fulfillmentTypeContainerDeliveryCharge) => (
                            <FulfillmentTypeContainerDeliveryChargeTableRow
                              key={fulfillmentTypeContainerDeliveryCharge.id}
                              fulfillmentTypeContainerDeliveryCharge={
                                fulfillmentTypeContainerDeliveryCharge
                              }
                            />
                          ),
                        )}
                      </>
                    )),
                  )}
                </TableLegacy>
              </TableLegacyContainer>
              <div className="flex items-center gap-4">
                {showNotSetWarning && (
                  <BadgeAmber className="mt-2" icon="ALERT">
                    Not set
                  </BadgeAmber>
                )}
                <Button
                  onClick={() =>
                    setFulfillmentTypeContainerDeliveryChargeDialogActive(true)
                  }
                  className={"mt-2"}
                  color={"tertiary"}
                >
                  <Icon icon={"PLUS_CIRCLE"} />
                  Add delivery charge
                </Button>
              </div>
            </CardContent>
          </Card>
          {method === "courier" && (
            <>
              <Card className="bg-white">
                <CardHeader
                  className={twMerge(
                    "bg-white",
                    "flex items-center justify-between p-4",
                  )}
                >
                  <div className="flex flex-col">
                    <H5>Courier delivery schedule</H5>
                    <H6>
                      Give your customers an accurate delivery date prediction
                      for orders fulfilled by courier. Your given dispatch and
                      delivery dates will be automatically pre-filled when
                      finalising courier orders.
                    </H6>
                  </div>
                  <Button
                    loading={
                      courierDeliveryScheduleQuery.isLoading &&
                      courierDeliveryScheduleQuery.fetchStatus !== "idle"
                    }
                    onClick={() =>
                      courierDeliverySchedule
                        ? setUpdateCourierScheduleActive(true)
                        : setCourierScheduleActive(true)
                    }
                    color={"secondary"}
                  >
                    <Icon icon={"PENCIL"} />
                    Edit
                  </Button>
                </CardHeader>
                <CardContent className={"p-4"}>
                  {courierDeliverySchedule ? (
                    <>
                      <div
                        className={"divide-y rounded-lg border border-solid"}
                      >
                        <CourierScheduleTableRow
                          title={"Shipping on"}
                          description={
                            courierDeliverySchedule.CourierDeliveryScheduleDay
                              .length ? (
                              deliveryAreaDaysOrdered(
                                courierDeliverySchedule.CourierDeliveryScheduleDay,
                              ).join(", ")
                            ) : (
                              <div
                                className={
                                  "flex flex-row items-center gap-1 text-amber-700"
                                }
                              >
                                <Icon icon={"CAUTION"} />
                                <p className={"font-semibold"}>
                                  Missing shipment days
                                </p>
                              </div>
                            )
                          }
                        />
                        <CourierScheduleTableRow
                          description={dayjs(
                            courierDeliverySchedule.orderCutOffTime,
                          )
                            .utc()
                            .format("HH:mm")}
                          title={"For orders finalised before"}
                        />
                        <CourierScheduleTableRow
                          description={
                            courierDeliverySchedule.dispatchDaysAfterConfirmation ===
                            0
                              ? "Same day"
                              : `${courierDeliverySchedule.dispatchDaysAfterConfirmation} working day(s) after finalisation`
                          }
                          title={"Dispatch date"}
                        />

                        <CourierScheduleTableRow
                          description={
                            courierDeliverySchedule.deliveryDaysAfterDispatch ===
                            0
                              ? "Same day"
                              : `${courierDeliverySchedule.deliveryDaysAfterDispatch} working day(s) after dispatch`
                          }
                          title={"Delivery date"}
                        />
                        <CourierScheduleTableRow
                          description={
                            !courierDeliverySchedule.startAt ||
                            dayjs(courierDeliverySchedule.startAt).isBefore(
                              dayjs(),
                            )
                              ? "Same day"
                              : `${dayjs(courierDeliverySchedule.startAt).utc().format("DD/MM/YYYY")}`
                          }
                          title={"Starting from"}
                        />
                      </div>
                      <CourierDeliveryScheduleActionPanel
                        className={"mt-2"}
                        orderCutOffTime={dayjs(
                          courierDeliverySchedule.orderCutOffTime,
                        )
                          .utc()
                          .format("HH:mm")}
                        dispatchDaysAfterConfirmation={
                          courierDeliverySchedule.dispatchDaysAfterConfirmation
                        }
                        deliveryDaysAfterDispatch={
                          courierDeliverySchedule.deliveryDaysAfterDispatch
                        }
                        selectedDays={courierDeliverySchedule.CourierDeliveryScheduleDay.map(
                          (CourierDeliveryScheduleDay) =>
                            CourierDeliveryScheduleDay.day,
                        )}
                        startAt={dayjs(
                          courierDeliverySchedule.startAt,
                        ).toDate()}
                        allowSaturdayDelivery={
                          courierDeliverySchedule.allowSaturdayDelivery
                        }
                      />
                    </>
                  ) : (
                    <p>Not set</p>
                  )}
                </CardContent>
              </Card>
              <CourierScheduleCreateDialog
                active={courierScheduleActive}
                onDismiss={() => setCourierScheduleActive(false)}
              />
              {courierDeliverySchedule && (
                <CourierScheduleUpdateDialog
                  active={updateCourierScheduleActive}
                  onDismiss={() => setUpdateCourierScheduleActive(false)}
                  courierDeliverySchedule={courierDeliverySchedule}
                />
              )}
            </>
          )}
          <FulfillmentTypeContainerDeliveryChargeCreateDialog
            active={fulfillmentTypeContainerDeliveryChargeDialogActive}
            onDismiss={() =>
              setFulfillmentTypeContainerDeliveryChargeDialogActive(false)
            }
            fulfillmentType={method}
            fulfillmentTypeId={fulfillmentTypeQuery.data?.pages[0]?.[0]?.id}
          />
          <FulfillmentTypeUpdateDialog
            active={flatChargeActive}
            onDismiss={() => setFlatChargeActive(false)}
            charge={fulfillmentTypeQuery.data?.pages[0]?.[0]?.charge}
            fulfillmentType={method}
            fulfillmentTypeId={fulfillmentTypeQuery.data?.pages[0]?.[0]?.id}
          />
          <FulfillmentTypeChargeCapUpdateDialog
            active={chargeCapActive}
            onDismiss={() => setChargeCapActive(false)}
            deliveryChargeCap={
              fulfillmentTypeQuery.data?.pages[0]?.[0]?.deliveryChargeCap
            }
            fulfillmentTypeId={fulfillmentTypeQuery.data?.pages[0]?.[0]?.id}
          />
        </>
      </PageContainer>
    </>
  );
};

const CourierScheduleTableRow = ({
  title,
  description,
}: {
  title: string;
  description: string | ReactNode;
}) => {
  return (
    <div className="grid grid-cols-2 p-2">
      <div className="col-span-1">
        <H6>{title}</H6>
      </div>
      <div className="col-span-1">
        <p>{description}</p>
      </div>
    </div>
  );
};

const ContainerEligibilityRuleRowMobile = ({
  name,
  containerType,
  unit,
  volume,
  id,
  FulfillmentTypeId,
}: {
  name: string;
  containerType: ContainerTypeEligibilityContainerTypesType;
  unit?: ContainerTypeEligibilityUnitTypesType;
  volume?: number;
  id?: number;
  FulfillmentTypeId: number;
}) => {
  const [edit, setEdit] = useState(false);

  const isActive = id ? true : false;
  const activeMessage = isActive ? "Active" : "Inactive";

  const formattedVolume = [volume ?? "", unit ?? "-"].join("");

  const formattedName = [name, formattedVolume].join(" • ");

  return (
    <>
      <div className="flex items-center justify-between p-4">
        <div className="flex flex-col">
          <span className="font-medium">{formattedName}</span>
          <span
            className={twMerge(
              "text-sm",
              isActive ? "text-emerald-500" : "text-red-500",
            )}
          >
            {activeMessage}
          </span>
        </div>
        <Button
          className="cursor-pointer p-1"
          size="small"
          color="transparent"
          onClick={() => setEdit(true)}
        >
          <Icon icon="PENCIL" size={0.8} />
        </Button>
      </div>
      <EditContainerEligibilityDialog
        active={edit}
        onDismiss={() => setEdit(false)}
        id={id}
        name={name}
        containerType={containerType}
        unit={unit}
        volume={volume}
        FulfillmentTypeId={FulfillmentTypeId}
      />
    </>
  );
};

const ContainerEligibilityRuleRowDesktop = ({
  name,
  containerType,
  unit,
  volume,
  id,
  FulfillmentTypeId,
  eligibilityGridCols,
  border,
}: {
  name: string;
  containerType: ContainerTypeEligibilityContainerTypesType;
  unit?: ContainerTypeEligibilityUnitTypesType;
  volume?: number;
  id?: number;
  FulfillmentTypeId: number;
  eligibilityGridCols: string;
  border: boolean;
}) => {
  const [edit, setEdit] = useState(false);

  return (
    <>
      <div className={twMerge("col-span-5 grid", eligibilityGridCols)}>
        {id ? (
          <div
            className={twMerge(
              "border-zinc-300 p-2 text-emerald-500",
              border && "border-b",
            )}
          >
            Active
          </div>
        ) : (
          <div
            className={twMerge(
              "border-zinc-300 p-2 text-red-500",
              border && "border-b",
            )}
          >
            Inactive
          </div>
        )}
        <div className={twMerge("border-zinc-300 p-2", border && "border-b")}>
          {name}
        </div>
        <div
          className={twMerge(
            "border-zinc-300 p-2 text-right",
            border && "border-b",
          )}
        >
          {volume ?? "-"}
        </div>
        <div
          className={twMerge(
            "border-zinc-300 p-2 text-right",
            border && "border-b",
          )}
        >
          {unit ?? "-"}
        </div>
        <div
          className={twMerge(
            "flex justify-end border-zinc-300 p-2",
            border && "border-b",
          )}
        >
          <Button
            className="cursor-pointer p-1"
            size="small"
            color="transparent"
            onClick={() => setEdit(true)}
          >
            <Icon icon="PENCIL" size={0.6} />
          </Button>
        </div>
      </div>
      <EditContainerEligibilityDialog
        active={edit}
        onDismiss={() => setEdit(false)}
        id={id}
        name={name}
        containerType={containerType}
        unit={unit}
        volume={volume}
        FulfillmentTypeId={FulfillmentTypeId}
      />
    </>
  );
};

const EditContainerEligibilityDialog = ({
  active,
  onDismiss,
  id,
  name,
  containerType,
  unit,
  volume,
  FulfillmentTypeId,
}: {
  active: boolean;
  onDismiss: () => void;
  id?: number;
  name: string;
  containerType: ContainerTypeEligibilityContainerTypesType;
  unit?: ContainerTypeEligibilityUnitTypesType;
  volume?: number;
  FulfillmentTypeId: number;
}) => {
  const [updatedVolume, setUpdatedVolume] = useState<number | undefined>(
    volume,
  );
  const [updatedUnit, setUpdatedUnit] =
    useState<ContainerTypeEligibilityUnitTypesType>(unit ?? "L");
  const [error, setError] = useState<string>("");
  const createContainerEligibilityRule =
    useContainerTypeEligibilityRulesCreate();
  const updateContainerEligibilityRule = useContainerTypeEligibilityRulesUpdate(
    id ?? 0,
  );
  const deleteContainerEligibilityRule = useContainerTypeEligibilityRulesDelete(
    id ?? 0,
  );

  const getVolumeMax = () => {
    if (updatedUnit === "L" && containerType === "plasticKeg") return 30;
    if (updatedUnit === "gal" && containerType === "plasticKeg") return 8;
    if (updatedUnit === "L" && containerType === "steelKeg") return 50;
    if (updatedUnit === "gal" && containerType === "steelKeg") return 10;
    if (updatedUnit === "L" && containerType === "cask") return 100;
    if (updatedUnit === "gal" && containerType === "cask") return 20;
    return 30;
  };

  const handleUpdate = () => {
    if (!updatedVolume || !updatedUnit) return;

    updateContainerEligibilityRule.mutate(
      {
        volume: updatedVolume,
        unit: updatedUnit,
      },
      {
        onSuccess: () => {
          onDismiss();
        },
      },
    );
  };

  const handleCreate = () => {
    if (!updatedVolume || !updatedUnit) return;

    createContainerEligibilityRule.mutate(
      {
        containerType,
        volume: updatedVolume,
        unit: updatedUnit,
        FulfillmentTypeId,
      },
      {
        onSuccess: () => {
          onDismiss();
        },
      },
    );
  };

  const onSubmit = () => {
    setError("");
    const maxVolume = getVolumeMax();
    if (!updatedVolume || updatedVolume < 1 || updatedVolume > maxVolume) {
      setError(
        `Please enter a valid volume between 1 and ${maxVolume}${updatedUnit}`,
      );
      return;
    }
    if (id) return handleUpdate();
    return handleCreate();
  };

  const onDelete = () => {
    deleteContainerEligibilityRule.mutate(undefined, {
      onSuccess: () => {
        setUpdatedVolume(undefined);
        setUpdatedUnit("L");
        onDismiss();
      },
    });
  };

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title="Edit eligibility rule"
        description="Define the maximum volume you’re able to fulfil by courier for this container type. Remove this rule if you’re unable to send containers of this type by courier."
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
      />
      <DialogContent className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <InputLabel>Container type</InputLabel>
          <InputContainer className="flex items-center justify-between">
            <span>{name}</span>
            <Icon icon="LOCK" />
          </InputContainer>
        </div>
        <div className="flex flex-wrap gap-4 md:flex-nowrap">
          <div className="flex w-full flex-col gap-1">
            <InputLabel>*Maximum volume</InputLabel>
            <InputContainer className="flex items-center justify-between">
              <InputNumber
                value={updatedVolume}
                onChange={(e) => setUpdatedVolume(parseFloat(e.target.value))}
                max={getVolumeMax()}
              />
            </InputContainer>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
          <div className="flex w-full flex-col gap-1">
            <InputLabel>*Unit</InputLabel>
            <InputContainer className="w-full">
              <InputSelect
                value={updatedUnit}
                onChange={(e) =>
                  setUpdatedUnit(
                    e.target.value as ContainerTypeEligibilityUnitTypesType,
                  )
                }
              >
                <InputOption value="L">Litres (L)</InputOption>
                <InputOption value="gal">Gallons (gal)</InputOption>
              </InputSelect>
            </InputContainer>
          </div>
        </div>
        <div className="flex flex-col gap-2 rounded-lg bg-zinc-100 p-4 text-sm">
          <span className="font-semibold">
            Your courier container eligibility rules:
          </span>
          <span className="flex items-center gap-2">
            <Icon
              icon="CIRCLE"
              size={0.5}
              className={twMerge(
                updatedUnit && updatedVolume
                  ? "text-emerald-500"
                  : "text-red-500",
              )}
            />
            {updatedUnit && updatedVolume
              ? `${name} can be fulfilled by courier up to and including ${updatedVolume}${updatedUnit}`
              : `${name} can only be fulfilled by haulier`}
          </span>
        </div>
      </DialogContent>
      <DialogCTA>
        {id && (
          <Button
            color="red"
            onClick={onDelete}
            loading={deleteContainerEligibilityRule.isPending}
          >
            Remove
          </Button>
        )}
        <Button
          disabled={!updatedUnit || !updatedVolume}
          onClick={onSubmit}
          loading={
            createContainerEligibilityRule.isPending ||
            updateContainerEligibilityRule.isPending
          }
        >
          Update
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
